import { useContext, useEffect, useState } from 'react';

import { DropdownMenuContext } from '../jdsdropdownmenu/context';
import { DropdownMenuProps } from '../jdsdropdownmenu/interface';
import { Icon } from '../jdsicon/generated';
import { handlePrevOrNextItemFocus } from '../jdsdropdownmenu/utils/dropdownmenu-logic';
import { useRootRef } from '../../hooks/useRootRef';
import { useScrollClose } from '../../hooks/useScrollClose';

export const getIcon = (icon?: string | JSX.Element) => {
  if (!icon) return undefined;
  if (typeof icon === 'string') return <Icon ic={icon} color='grey-80' />;
  if (icon.props?.ic) return <Icon ic={icon.props.ic} color='grey-80' />;
  return <Icon ic={icon} color='grey-80' />;
};

const DROPDOWNMENU_SELECTOR = 'div.j-JDSDropdownMenuInternal-menu-container';

export const withLogic =
  (
    Component: React.ComponentType<DropdownMenuProps>
  ): React.FC<DropdownMenuProps> =>
  (props: DropdownMenuProps) => {
    const { activeMenuId, setActiveMenuId } = useContext(DropdownMenuContext);
    const { element, rootRef } = useRootRef();

    const [menuVisible, setMenuVisible] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);

    const { anchorEl, position } = props;

    const dropdownMenuRef = element?.querySelector<HTMLDivElement>(
      DROPDOWNMENU_SELECTOR
    );

    // position calculation
    useEffect(() => {
      if (props.open) {
        setTimeout(() => {
          setInternalOpen(true);

          setMenuVisible(true);
        }, 10);
      } else {
        setMenuVisible(false);

        setTimeout(() => {
          setInternalOpen(false);
        }, 300); // --transitions-duration-rapid
      }
    }, [props.open, anchorEl, position, dropdownMenuRef]);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (props.open && !activeMenuId) {
          switch (event.key) {
            case 'ArrowUp': {
              handlePrevOrNextItemFocus(
                -1,
                dropdownMenuRef,
                '.j-JDSDropdownMenuItem-container'
              );
              event.preventDefault();
              event.stopPropagation();
              break;
            }
            case 'ArrowDown': {
              handlePrevOrNextItemFocus(
                1,
                dropdownMenuRef,
                '.j-JDSDropdownMenuItem-container'
              );
              event.preventDefault();
              event.stopPropagation();
              break;
            }
            case 'Escape': {
              props?.onClose?.();
              anchorEl?.current?.focus?.();
              break;
            }
          }
        }
      };
      if (props.open) {
        window.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [props.open, dropdownMenuRef, activeMenuId]);

    const shouldShowSuffix = props?.searchConfig?.suffix !== false;
    const close = () => {
      props?.onClose?.();
      setMenuVisible(false);
      setActiveMenuId(undefined);
    };
    useScrollClose(close);
    return (
      <Component
        {...{
          ...props,
          open: internalOpen,
          onClose: () => {
            props?.onClose?.();
            setMenuVisible(false);
            setActiveMenuId(undefined);
          },
          rootRef,
          searchSuffix: shouldShowSuffix ? 'IcMic' : undefined,
          searchLabel:
            props.searchLabel || props?.searchConfig?.search || 'Search',
          searchValue: props.searchValue || props?.searchConfig?.value,
          onSuffixClick:
            props.onSuffixClick || props?.searchConfig?.onSuffixClick,
          onSearchChange: props?.onSearchChange,
          _onContainerClick: (event: MouseEvent) => {
            event.stopPropagation();
          },
          _menuContainerVisible: menuVisible
        }}
      />
    );
  };

export type { DropdownMenuProps };
